/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, StyledModal, Box, Text, Heading, Stack } from 'sarsaparilla';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';

import SelectCustomerModal from 'ui-field-sales/src/components/cart/SelectCustomerModal';

import { readRuleValue, issuanceStatuses, getClassName } from '../../src/utils/utilsNew';
import {
    PERMIT_ISSUED,
    PERMIT_COMPLETE,
    LOTTERY_AWARDED,
    LOTTERY_APPLIED,
    PERMIT_IN_REVIEW,
    PERMIT_REVIEWED,
    LOTTERY_AWARDED_IN_PROGRESS,
    PERMIT_IN_PROGRESS,
    GROUP_DETAIL_ALT_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_ALT_CONFIRM_PROP_PATH,
} from '../constants';

import { BEAUTIFY_ENTRY_TYPES } from '../utils/constants';

import AlternatePermitHolderNameEmailEntry from './AlternatePermitHolderNameEmailEntry';
import AlternatePermitHolderNamePhoneEntry from './AlternatePermitHolderNamePhoneEntry';

const defaultDataBindingMap = {
    [GROUP_DETAIL_ALT_PROP_PATH]: 'alt_group_leaders',
    [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
    [GROUP_DETAIL_ALT_CONFIRM_PROP_PATH]: 'is_confirmed',
};

const defaultAttributes = {};

export default (
    AlternatePermitHolderEntry,
    issuanceShape,
    dataBindingsMap = defaultDataBindingMap,
    attributes = defaultAttributes
) => {
    function AlternatePermitHolder({
        pActions,
        reservation,
        maxAlternates,
        permit,
        entryType,
        hasPermission,
        isInternal,
        isReadOnly,
        roleType,
        displayValidationError,
        validation,
        paths,
        addFieldRef,
        alternateModalSections,
        alternateMessageDetails,
        hideAltPermitHolderBox = false,
    }) {
        const [modalIsOpen, setModalIsOpen] = useState(false);
        const [customerSelectionModalOpen, setCustomerSelectionModalOpen] =
            useState(false);

        const closeModal = () => setModalIsOpen(false);
        const openModal = () => setModalIsOpen(true);

        const addLeader = (leader = {}) => {
            if (!leader.walkUp) {
                let newGroupDetailAlt = get(
                    reservation,
                    dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]
                );
                // to avoid errors when it's null
                if (isNil(newGroupDetailAlt)) {
                    newGroupDetailAlt = [];
                }
                const newLeader = {
                    account_id: leader.account_id,
                    first_name: leader.first_name,
                    email: leader.email,
                    cell_phone: leader.cell_phone,
                    last_name: leader.last_name,
                };

                const maxAlternateLeaders =
                    maxAlternates ?? readRuleValue('MaxAlternateLeaders', permit.rules);
                const currentCount = newGroupDetailAlt.length;
                const condition = currentCount + 1 <= maxAlternateLeaders;
                if (condition) {
                    const addLeaderData = {
                        key: `${reservation.id}.${currentCount}`,
                        ...newLeader,
                    };
                    newGroupDetailAlt.push(addLeaderData);
                }
                pActions.updatePendingReservation(
                    reservation.id,
                    dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH],
                    newGroupDetailAlt
                );
            }
        };

        const removeLeader = (index) => {
            const newGroupDetailAlt = get(
                reservation,
                dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH],
                []
            );
            if (newGroupDetailAlt.length > 0) {
                newGroupDetailAlt.splice(index, 1);
                pActions.updatePendingReservation(
                    reservation.id,
                    dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH],
                    newGroupDetailAlt
                );
            }
        };

        const handleModifyLeader = (index, leader) => {
            const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}]`;
            pActions.updatePendingReservation(reservation.id, path, leader);
        };

        const handleAlternateLeaderValueChange = (index, key, value) => {
            const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${entryType}`;
            pActions.updatePendingReservation(reservation.id, path, value);
        };

        const handleAlternateLeaderValidation = (index, isInValid, error) => {
            const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${entryType}`;
            if (isInValid) {
                pActions.formValidationChange(`${reservation.id}_${path}`, error);
            } else {
                pActions.formValidationChange(`${reservation.id}_${path}`, null);
            }
        };

        const handleAlternateLeaderConfirm = (index) => {
            pActions.updatePendingReservation(
                reservation.id,
                `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${dataBindingsMap[GROUP_DETAIL_ALT_CONFIRM_PROP_PATH]}`,
                true
            );
        };

        const alternateLeaderValidatorMethod = (index, value) => {
            if (isEmpty(value)) {
                return false;
            }

            if (entryType === 'email' && !isEmail(value)) {
                return true;
            }

            if (entryType === 'cell_phone' && !isMobilePhone(value)) {
                return true;
            }

            return (
                isEqual(
                    get(
                        reservation,
                        `${dataBindingsMap[GROUP_DETAIL_PROP_PATH]}.${entryType}`,
                        ''
                    ),
                    value
                ) ||
                get(reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []).filter(
                    (alt) =>
                        entryType === 'email'
                            ? isEqual(alt.email, value)
                            : isEqual(alt.cell_phone, value)
                ).length > 1
            );
        };

        const addNewAlternateLeader = () => {
            if (
                AlternatePermitHolderEntry?.name === 'AlternatePermitHolderInternalEntry'
            ) {
                setCustomerSelectionModalOpen(true);
            } else {
                addLeader();
            }
        };

        const isDisabled = (status) => {
            const { lottery_awarded: isLotteryAwarded } = reservation ?? {};
            if (hasPermission && isInternal && !isReadOnly && roleType !== 'ROLE_CSR2') {
                return false;
            }

            return (
                status === issuanceStatuses.get(PERMIT_ISSUED) ||
                status === issuanceStatuses.get(PERMIT_COMPLETE) ||
                status === issuanceStatuses.get(PERMIT_IN_REVIEW) ||
                status === issuanceStatuses.get(PERMIT_REVIEWED) ||
                status === issuanceStatuses.get(LOTTERY_APPLIED) ||
                status === issuanceStatuses.get(LOTTERY_AWARDED) ||
                status === issuanceStatuses.get(LOTTERY_AWARDED_IN_PROGRESS) ||
                (isLotteryAwarded &&
                    status === issuanceStatuses.get(PERMIT_IN_PROGRESS)) ||
                isReadOnly
            );
        };

        const renderAltLeaders = () => {
            const { id, status } = reservation;
            const groupDetailAlt = get(
                reservation,
                dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH],
                []
            );

            if (!groupDetailAlt || groupDetailAlt.length === 0) {
                return null;
            }
            return groupDetailAlt.map((leader, index) => {
                const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${entryType}`;
                const className = getClassName(
                    get(validation, `${id}_${path}`),
                    displayValidationError
                );
                const altConfirm = dataBindingsMap[GROUP_DETAIL_ALT_CONFIRM_PROP_PATH];
                if (AlternatePermitHolderEntry) {
                    return (
                        <AlternatePermitHolderEntry
                            key={leader.key}
                            leaderId={leader.key}
                            leader={leader}
                            index={index}
                            addFieldRef={addFieldRef}
                            isDisabled={isDisabled(status)}
                            handleAlternateLeaderChange={handleAlternateLeaderValueChange}
                            handleAlternateLeaderValidation={() =>
                                handleAlternateLeaderValidation(index)
                            }
                            alternateLeaderValidatorMethod={() =>
                                alternateLeaderValidatorMethod(index)
                            }
                            handleAlternateLeaderConfirm={() =>
                                handleAlternateLeaderConfirm(index)
                            }
                            handleModifyLeader={handleModifyLeader}
                            className={className}
                            removeLeader={removeLeader}
                            pActions={pActions}
                            reservationId={reservation.id}
                            paths={paths}
                            altConfirm={altConfirm}
                        />
                    );
                }

                return entryType === 'email' ? (
                    <AlternatePermitHolderNameEmailEntry
                        key={leader.key}
                        leaderId={leader.key}
                        leader={leader}
                        index={index}
                        isDisabled={isDisabled(status)}
                        handleAlternateLeaderChange={handleAlternateLeaderValueChange}
                        handleAlternateLeaderValidation={() =>
                            handleAlternateLeaderValidation(index)
                        }
                        alternateLeaderValidatorMethod={() =>
                            alternateLeaderValidatorMethod(index)
                        }
                        handleAlternateLeaderConfirm={() =>
                            handleAlternateLeaderConfirm(index)
                        }
                        className={className}
                        removeLeader={removeLeader}
                        pActions={pActions}
                        reservationId={reservation.id}
                    />
                ) : (
                    <AlternatePermitHolderNamePhoneEntry
                        key={leader.key}
                        leaderId={leader.key}
                        leader={leader}
                        index={index}
                        isDisabled={isDisabled(status)}
                        handleAlternateLeaderChange={handleAlternateLeaderValueChange}
                        handleAlternateLeaderValidation={() =>
                            handleAlternateLeaderValidation(index)
                        }
                        alternateLeaderValidatorMethod={() =>
                            alternateLeaderValidatorMethod(index)
                        }
                        handleAlternateLeaderConfirm={() =>
                            handleAlternateLeaderConfirm(index)
                        }
                        className={className}
                        removeLeader={removeLeader}
                        pActions={pActions}
                        reservationId={reservation.id}
                    />
                );
            });
        };

        const renderModal = () => {
            const sections = alternateModalSections.length
                ? alternateModalSections
                : [
                      {
                          title: 'Why An Alternate Permit Holder?',
                          paragraph:
                              'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
                      },
                      {
                          title: 'How to Add an Alternate Permit Holder',
                          paragraph: `Enter first name, last name and ${BEAUTIFY_ENTRY_TYPES[entryType]} for an alternate permit holder to be added to permit. Once the permit is purchased, the designated alternate(s) cannot be modified.`,
                      },
                  ];

            return (
                <StyledModal
                    size="md"
                    isOpen={modalIsOpen}
                    heading="Alternate Permit Holders"
                    onRequestClose={closeModal}
                >
                    <Stack>
                        {sections.map(({ title, paragraph }) => (
                            <Box>
                                <Heading headingLevel={2} appearance="h6">
                                    {title}
                                </Heading>
                                <Text>{paragraph}</Text>
                            </Box>
                        ))}
                    </Stack>
                </StyledModal>
            );
        };

        const maxAlternateLeaders =
            maxAlternates || readRuleValue('MaxAlternateLeaders', permit.rules);
        const { status } = reservation;
        const newGroupDetailAlt =
            get(reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []) ?? [];

        return (
            !hideAltPermitHolderBox && (
                <div className="per-section-group">
                    <div className="leader-container">{renderModal()}</div>
                    <div className="permit-alt-leader">
                        <div className="permit-alt-leader-form">
                            <div className="permit-alt-leader-msg">
                                <Icon iconName="info" />
                                <p>
                                    You may select a max of {maxAlternateLeaders} other
                                    people to act as alternate permit holders on your
                                    trip.&nbsp;
                                    {alternateMessageDetails !== null &&
                                    alternateMessageDetails !== undefined
                                        ? alternateMessageDetails
                                        : `Please enter a valid ${BEAUTIFY_ENTRY_TYPES[entryType]} for each of the intended holders.`}
                                    &nbsp;See{' '}
                                    <button
                                        className="rec-button-link"
                                        onClick={openModal}
                                        type="button"
                                    >
                                        Alternate Permit Holders
                                    </button>{' '}
                                    for more information.
                                </p>
                            </div>
                            {renderAltLeaders()}
                        </div>
                        <div className="permit-add-icon">
                            <Button
                                isDisabled={
                                    isDisabled(status) ||
                                    newGroupDetailAlt.length >= maxAlternateLeaders
                                }
                                appearance="link"
                                onClick={addNewAlternateLeader}
                                iconBeforeElement={<Icon iconName="add-circle" />}
                            >
                                Add alternate permit holder ({maxAlternateLeaders} max)
                            </Button>
                        </div>
                    </div>
                    <SelectCustomerModal
                        title="Select Alternate Permit Holder"
                        isOpen={customerSelectionModalOpen}
                        closeModal={() => setCustomerSelectionModalOpen(false)}
                        selectCustomer={addLeader}
                    />
                </div>
            )
        );
    }

    AlternatePermitHolder.propTypes = {
        pActions: PropTypes.shape({
            formValidationChange: PropTypes.func.isRequired,
            updatePendingReservation: PropTypes.func.isRequired,
        }).isRequired,
        permit: PropTypes.shape({
            rules: PropTypes.arrayOf(PropTypes.shape({})),
        }).isRequired,
        validation: PropTypes.shape({}).isRequired,
        alternateMessageDetails: PropTypes.node,
        alternateModalSections: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string,
                paragraph: PropTypes.node,
            })
        ),
        displayValidationError: PropTypes.bool,
        hasPermission: PropTypes.bool,
        isInternal: PropTypes.bool,
        maxAlternates: PropTypes.number,
        reservation: issuanceShape,
        paths: PropTypes.shape({
            email: PropTypes.string,
            last_name: PropTypes.string,
            first_name: PropTypes.string,
            alt_group_detail: PropTypes.string,
        }),
        entryType: PropTypes.string,
        isReadOnly: PropTypes.bool,
        roleType: PropTypes.string,
    };

    AlternatePermitHolder.defaultProps = {
        alternateMessageDetails: null,
        alternateModalSections: [],
        hasPermission: true,
        isInternal: false,
        entryType: 'email',
    };

    AlternatePermitHolder.attributes = attributes;

    AlternatePermitHolder.componentName = 'AlternatePermitHolder';

    return AlternatePermitHolder;
};
